
import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ActionLine from './ActionLine';
import type { ActionPhrase } from '@/types/actions';

const ActionBanner = () => {
  const { data: phrases, isLoading, error } = useQuery({
    queryKey: ['actionPhrases'],
    queryFn: async () => {
      console.log('🎯 ActionBanner: Starting to fetch action phrases...');
      
      const { data, error } = await supabase
        .from('action_phrases')
        .select(`
          *,
          categories!inner(
            id,
            name,
            slug
          )
        `)
        .order('line_number, display_order');
      
      if (error) {
        console.error('❌ ActionBanner: Error fetching action phrases:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('⚠️ ActionBanner: No action phrases found in the database');
        return [];
      }

      // Process and validate the data
      const validData = data.filter((phrase: any) => {
        const isValid = phrase && 
                       typeof phrase.text === 'string' && 
                       phrase.category_id && 
                       phrase.categories;
        
        if (!isValid) {
          console.warn('⚠️ ActionBanner: Invalid phrase data:', phrase);
        } else {
          console.log('✅ ActionBanner: Valid phrase:', {
            text: phrase.text,
            category_id: phrase.category_id,
            category_slug: phrase.categories?.slug,
            category_name: phrase.categories?.name
          });
        }
        
        return isValid;
      }).map((phrase: any) => ({
        ...phrase,
        category_slug: phrase.categories.slug // Flatten for easier access
      }));
      
      console.log('📊 ActionBanner: Processed phrases:', {
        total: data.length,
        valid: validData.length,
        byLine: validData.reduce((acc: Record<number, number>, phrase) => {
          const lineNum = phrase.line_number || 0;
          acc[lineNum] = (acc[lineNum] || 0) + 1;
          return acc;
        }, {})
      });
      
      return validData;
    }
  });

  useEffect(() => {
    if (error) {
      console.error('❌ ActionBanner query error:', error);
    }
    
    if (phrases && phrases.length > 0) {
      console.log('🎯 ActionBanner: Final phrases ready for display:', phrases.length);
      phrases.forEach((phrase, index) => {
        console.log(`📝 Phrase ${index + 1}:`, {
          text: phrase.text,
          line: phrase.line_number,
          categoryId: phrase.category_id,
          categorySlug: phrase.category_slug
        });
      });
    }
  }, [phrases, error]);

  if (isLoading) {
    console.log('⏳ ActionBanner: Loading action phrases...');
    return null;
  }
  
  if (!phrases || phrases.length === 0) {
    console.log('❌ ActionBanner: No phrases available to display');
    return null;
  }

  // Group phrases by line number
  const phrasesByLine: Record<number, ActionPhrase[]> = {};
  phrases.forEach(phrase => {
    if (phrase.line_number >= 1 && phrase.line_number <= 3) {
      if (!phrasesByLine[phrase.line_number]) {
        phrasesByLine[phrase.line_number] = [];
      }
      phrasesByLine[phrase.line_number].push(phrase);
    }
  });

  console.log('🎬 ActionBanner: Rendering with phrases by line:', Object.keys(phrasesByLine).length, 'lines');

  return (
    <div className="w-full max-w-[95vw] mx-auto px-4 py-4 rounded-xl bg-gradient-to-br from-background/80 to-background/40 backdrop-blur-sm border border-primary/10 shadow-xl overflow-hidden">
      <div className="space-y-3">
        {[1, 2, 3].map((lineNumber) => (
          phrasesByLine[lineNumber] && phrasesByLine[lineNumber].length > 0 ? (
            <ActionLine
              key={lineNumber}
              phrases={phrasesByLine[lineNumber]}
              rowIndex={lineNumber}
            />
          ) : null
        ))}
      </div>
    </div>
  );
};

export default ActionBanner;
